import React, { useEffect, useMemo, useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../redux/store';
import { useForm } from 'react-hook-form';
import AddressAutoComplete from '../Organisation/Portal/AddressAutoComplete';
import { FormTextField } from '../../components/ui/atoms/FormTextField';
import { FormMuiTelInput } from '../../components/ui/atoms/FormMuiTelInput';
import { useNavigate } from 'react-router-dom';
import LoadingButton from '@mui/lab/LoadingButton';
import { Auth } from 'aws-amplify';
import SelectAvatarDialog from '../../components/ui/templates/profile/SelectAvatarDialog';
import { signUpStepsNoMfa, UserProfile } from '../../types/user';
import { RequestStatus } from '../../redux/utils';
import { readCurrentUser, updateUser } from '../../redux/slices/user';
import ProfilePicture from '../../components/ui/atoms/ProfilePicture';
import { BucketImage } from '../../hooks/useBucketImage';
import { Country, ICountry } from 'country-state-city';
import { LocationSelector } from '../../components/ui/molecules/LocationSelector';
import {
	alpha,
	Box,
	Button,
	Card,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Stack,
	Typography
} from '@mui/material';
import { HomeRounded, MailRounded, PersonRounded, PhotoCameraRounded, LanguageRounded } from '@mui/icons-material';
import FileUploader from '../../components/ui/organisms/FileUploader';
import { FormDatePicker } from '../../components/ui/atoms/FormDatePicker';
import { FormSelect } from '../../components/ui/atoms/FormSelect';
import EducationHistoryEditor from '../../components/ui/molecules/EducationHistoryEditor';
import { resolveEducationJurisdiction } from '../../utils/educationHistory';
import PageHeader from '../../components/layout/PageHeader';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { firstInvalidField, paneForField, PANE_LABELS } from './editPanes';
import type { EditPane } from './editPanes';
import { shouldSyncField } from './profileSync';
import { currencyForCountry } from './currencyForCountry';
import { useCurrency } from '../../hooks/useCurrency';
import {
	SUPPORTED_LANGUAGES,
	detectInitialLanguage,
	normaliseLanguage,
	setPreferredLanguage
} from '../../utils/language';
import CustomStepper from '../../components/ui/organisms/CustomStepper';
import PillTabs from '../../components/ui/atoms/PillTabs';
import { useLocation } from 'react-router';
import { fetchPartnerPersonalization } from '../../redux/slices/partnerBranding';
import { readPartnerSlug } from '../../hooks/usePartnerBranding';

// Tinted icon badge that leads each section — shared design-language primitive.
const SectionIcon = ({ children }: { children: React.ReactNode }) => (
	<Box
		sx={{
			flexShrink: 0,
			width: 40,
			height: 40,
			borderRadius: 2.5,
			display: 'grid',
			placeItems: 'center',
			color: 'primary.main',
			bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12)
		}}
	>
		{children}
	</Box>
);

const SectionHeader = ({
	icon,
	title,
	description
}: {
	icon: React.ReactNode;
	title: string;
	description?: string;
}) => (
	<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: { xs: 2, sm: 3 }, pt: 1.5, pb: 0.5 }}>
		<SectionIcon>{icon}</SectionIcon>
		<Box sx={{ minWidth: 0 }}>
			<Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
				{title}
			</Typography>
			{description && (
				<Typography variant="caption" color="text.secondary">
					{description}
				</Typography>
			)}
		</Box>
	</Box>
);

// A responsive row of form fields — side by side on sm+, stacked full-width on xs.
const FieldRow = ({ children }: { children: React.ReactNode }) => (
	<Box
		sx={{
			display: 'flex',
			flexDirection: { xs: 'column', sm: 'row' },
			alignItems: 'flex-start',
			gap: { xs: 1.5, sm: 2 },
			px: { xs: 2, sm: 3 }
		}}
	>
		{children}
	</Box>
);

const EditProfile = () => {
	const dispatch = useAppDispatch();
	const { state } = useLocation();

	const countries: ICountry[] = useMemo(() => {
		return Country.getAllCountries();
	}, []);
	const { currentUser: user, updateUserRequest } = useAppSelector((state) => state.user);
	const steps = signUpStepsNoMfa;

	// The router guard sends unconfirmed users here with `signUp` in location state,
	// but that state is lost on any further navigation or a refresh. Fall back to the
	// account's own confirmed flag — the same condition the guard redirects on — so a
	// half-finished sign-up cannot end up in the full tabbed editor. The stepper and
	// currency select already keyed off `confirmed`, which is how the two layouts
	// could previously be rendered at the same time.
	// `user` is undefined until the profile loads; an absent record is not a sign-up.
	const isSignUp = Boolean(state?.signUp) || (!!user && !user.confirmed);

	const { handleSubmit, setValue, watch, control, getValues } = useForm<UserProfile>({
		defaultValues: {
			email: user?.email ?? '',
			firstName: user?.firstName ?? '',
			lastName: user?.lastName ?? '',
			preferredName: user?.preferredName ?? '',
			citizenship: user?.citizenship ?? '',
			currency: user?.currency ?? '',
			// Normalise any stored tag (e.g. `en-us`, `en_US`, or an unsupported
			// locale) to a supported dialect so the FormSelect value always matches an
			// available option and we never persist an unbundled language.
			language: user?.language ? normaliseLanguage(user.language) : detectInitialLanguage(),
			address: user?.address ?? {
				line1: '',
				line2: '',
				city: '',
				state: '',
				country: '',
				zip: '',
				latitude: user?.address?.latitude ?? '',
				longitude: user?.address?.longitude ?? '',
				isoCountryCode: user?.address?.isoCountryCode ?? ''
			},
			dateOfBirth: user?.dateOfBirth ?? '',
			educationHistory: user?.educationHistory ?? [],
			phone: user?.phone ?? '',
			profilePicture: user?.profilePicture ?? {}
		}
	});

	const formWatch = watch();

	const [temporaryPicture, setTemporaryPicture] = useState<BucketImage>();

	// Pill-tab panes (mirrors Preferences). Panes are hidden, NOT unmounted, so
	// react-hook-form keeps every field registered and validation covers the
	// whole form on submit regardless of the visible pane. Sign-up keeps the
	// linear stepper layout.
	const [activePane, setActivePane] = useState<EditPane>('profile');
	const paneSx = (pane: EditPane) => (isSignUp ? {} : { display: activePane === pane ? 'block' : 'none' });

	useEffect(() => {
		if (!user) return;
		for (const field of [
			'email',
			'firstName',
			'lastName',
			'preferredName',
			'address',
			'currency',
			'language',
			'dateOfBirth',
			'educationHistory',
			'phone',
			'profilePicture'
		]) {
			if (field === 'language') {
				// Normalise the stored tag (e.g. `en_US` → `en-US`) and fall back to a
				// detected default when the user has no saved preference, so we never
				// overwrite the select with an unbundled dialect or `undefined`.
				setValue('language', user.language ? normaliseLanguage(user.language) : detectInitialLanguage());
				continue;
			}

			// The field list is iterated as strings, so the lookup is genuinely dynamic.
			// `User` and `UserProfile` do not have identical shapes, so index through a
			// record rather than asserting a key that may not exist on both.
			const incoming = (user as unknown as Record<string, unknown>)[field];

			// A re-sync fires whenever `currentUser`'s identity changes, which several
			// reducers do without touching the profile. Skip the fields the server has
			// no value for so the re-sync cannot blank them — see profileSync.ts.
			if (!shouldSyncField(incoming, getValues(field as keyof UserProfile))) {
				continue;
			}

			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			setValue(field as keyof UserProfile, incoming);
		}
	}, [user]);

	// Partner personalization: when the new user matched a row on their organisation's
	// constituent roster, seed their name from it so sign-up feels pre-filled.
	// Deliberately only fills EMPTY fields — a value the user (or an earlier sync)
	// already provided always wins, so this can never clobber real data.
	const personalization = useAppSelector((state) => state.partnerBranding.personalization);

	useEffect(() => {
		if (personalization === undefined) {
			dispatch(fetchPartnerPersonalization(readPartnerSlug()));
		}
	}, [dispatch, personalization]);

	useEffect(() => {
		const rosterName = personalization?.rosterName?.trim();
		if (!rosterName) return;

		const parts = rosterName.split(/\s+/);
		const first = parts[0];
		const last = parts.slice(1).join(' ');

		if (first && !getValues('firstName')) {
			setValue('firstName', first);
		}
		if (last && !getValues('lastName')) {
			setValue('lastName', last);
		}
	}, [personalization, getValues, setValue]);

	const loading = useMemo(() => updateUserRequest.status === RequestStatus.PENDING, [updateUserRequest.status]);
	const [confirmEmailOpen, setConfirmEmailOpen] = useState(false);
	const [countryISO, setCountryISO] = useState('');
	const { setCurrency } = useCurrency();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const submitProfile = async (data: UserProfile) => {
		await dispatch(updateUser(data)).then(() => {
			dispatch(readCurrentUser());
			// Completing the profile is the last step of sign-up, so it hands off to the
			// dashboard rather than the profile view. The onboarding screen used to do
			// this; without it a new account would come to rest on its own profile page
			// and never reach the homepage that offers the upgrade prompt.
			navigate(isSignUp ? '/' : '/profile');
		});
	};
	const handleChangeEmail = async () => {
		const val = getValues();
		submitProfile(val);
		setConfirmEmailOpen(false);
	};
	const handleCancelChangeEmail = async () => {
		setValue('email', user?.email ?? '');
		setConfirmEmailOpen(false);
	};
	const onSubmit = handleSubmit(
		async (data) => {
			if (data.email === user?.email) {
				submitProfile(data);
			} else {
				setConfirmEmailOpen(true);
			}
		},
		(err) => {
			console.error(err);

			// Panes are hidden rather than unmounted, so every field stays registered
			// and validation covers the whole form. The consequence is that a required
			// field on a pane the user cannot see blocks the save while its error
			// message renders inside `display: none`: Save appears to do nothing at
			// all. Bring the offending pane into view and say what is wrong.
			const firstInvalid = firstInvalidField(err);
			const pane = firstInvalid ? paneForField(firstInvalid) : undefined;
			if (pane && !isSignUp) {
				setActivePane(pane);
			}
			enqueueSnackbar(
				pane && pane !== activePane
					? t(
							'pages.profile.edit.validationOnOtherPane',
							'Something needs your attention on the {{pane}} tab before this can be saved.',
							{ pane: PANE_LABELS[pane] }
					  )
					: t('pages.profile.edit.validationFailed', 'Please complete the highlighted fields before saving.'),
				{ variant: 'warning' }
			);
		}
	);

	const handleSelectedAddress = (address: any) => {
		const getCountry = countries.find((country) => country.isoCode === address.country.substring(0, 2));
		setValue('address.isoCountryCode', getCountry?.isoCode);
		setValue('address.line1', address.street || '');
		if (!address.street) {
			setValue('address.line2', address.municipality || '');
		} else {
			setValue('address.line2', '');
		}
		setValue('address.city', address.subRegion || '');
		setValue('address.zip', address.postalCode || '');
		setValue('address.country', getCountry?.name.trim());
		setValue('address.state', address.region || '');
		setValue('address.longitude', address.geometry.point[0]);
		setValue('address.latitude', address.geometry.point[1]);
	};

	// Solid-primary call-to-action — overrides the global glass button style.
	const saveButtonSx = {
		px: 3,
		py: 1,
		borderRadius: 999,
		fontWeight: 600,
		textTransform: 'none',
		color: 'primary.contrastText',
		bgcolor: 'primary.main',
		border: 'none',
		boxShadow: (theme: any) => `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
		'&:hover': {
			bgcolor: 'primary.dark',
			border: 'none',
			boxShadow: (theme: any) => `0 10px 28px ${alpha(theme.palette.primary.main, 0.4)}`,
			transform: 'translateY(-2px)'
		}
	};

	// Quiet ghost button for the secondary action.
	const cancelButtonSx = {
		px: 3,
		py: 1,
		borderRadius: 999,
		fontWeight: 600,
		textTransform: 'none',
		color: 'text.secondary',
		bgcolor: 'transparent',
		border: 'none',
		boxShadow: 'none',
		'&:hover': {
			bgcolor: (theme: any) => alpha(theme.palette.text.primary, 0.06),
			border: 'none',
			boxShadow: 'none'
		}
	};

	// Soft tinted-primary pill for the two profile-image actions (Browse / Pick from gallery).
	// They are equal-weight secondary actions, so they share one calm style.
	// Shared platform button styles (match the AI Assistant / System screens).
	const PRIMARY_BUTTON_SX = {
		textTransform: 'none',
		px: 2.5,
		py: 0.75,
		borderRadius: 1.5,
		bgcolor: 'primary.main',
		color: 'common.white',
		border: 'none',
		boxShadow: 'none',
		backdropFilter: 'none',
		'& .MuiSvgIcon-root': { color: 'common.white' },
		'&:hover': { bgcolor: 'primary.dark', boxShadow: 'none', transform: 'none' }
	};

	const SECONDARY_BUTTON_SX = {
		textTransform: 'none',
		px: 2,
		py: 0.75,
		borderRadius: 1.5,
		bgcolor: 'transparent',
		border: '1px solid',
		borderColor: 'divider',
		color: 'text.secondary',
		boxShadow: 'none',
		backdropFilter: 'none',
		'& .MuiSvgIcon-root': { color: 'text.secondary' },
		'&:hover': { bgcolor: 'action.hover', borderColor: 'text.secondary', boxShadow: 'none', transform: 'none' }
	};

	return (
		<Box sx={{ pt: 1, width: { xs: '100%', md: '100%' }, display: 'flex', flexDirection: 'column', gap: 2 }}>
			{!user?.confirmed && <CustomStepper steps={steps} activeStep={2} sx={{ mt: 4 }} />}
			<PageHeader title="Edit Account" />

			{!isSignUp && (
				<PillTabs
					value={activePane}
					onChange={(value) => setActivePane(value as EditPane)}
					options={[
						{ value: 'profile', label: 'Profile' },
						{ value: 'education', label: 'Education' },
						{ value: 'address', label: 'Address' },
						{ value: 'contact', label: 'Contact' },
						{ value: 'preferences', label: 'Preferences' }
					]}
				/>
			)}

			<Card sx={{ p: 0, overflow: 'hidden', '&:hover': { transform: 'none' } }}>
				<form onSubmit={onSubmit}>
					{/* Identity header — avatar, name and email. */}
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: { xs: 2, sm: 3 }, py: 1.5 }}>
						<ProfilePicture
							aria-label="account of current user"
							sx={{ width: '3.5rem', height: '3.5rem', fontSize: '2rem' }}
							image={temporaryPicture ?? formWatch.profilePicture}
							profile={user}
						/>
						<Box sx={{ minWidth: 0 }}>
							<Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
								{formWatch.firstName} {formWatch.lastName}
							</Typography>
							{user?.email && (
								<Typography variant="body2" color="text.secondary" noWrap>
									{user.email}
								</Typography>
							)}
						</Box>
					</Box>

					<Divider />

					<Box sx={paneSx('profile')}>
						{!isSignUp && (
							<>
								{/* Profile image. */}
								<SectionHeader
									icon={<PhotoCameraRounded />}
									title="Profile image"
									description="Upload a photo or choose a ready-made avatar."
								/>
								<Box
									sx={{
										px: { xs: 2, sm: 3 },
										pb: 1.5
									}}
								>
									<FileUploader
										hideLabel
										compact
										browseButtonSx={PRIMARY_BUTTON_SX}
										extraActions={
											<SelectAvatarDialog
												triggerSx={SECONDARY_BUTTON_SX}
												onSelect={(avatar) => {
													setValue('profilePicture', avatar);
													setTemporaryPicture(avatar);
												}}
											/>
										}
										acceptedFileTypes={['.png', '.jpeg', '.jpg', '.svg']}
										accessLevel="protected"
										processFile={({ file }) => {
											return {
												key: 'profilePic/customProfilePicture',
												file
											};
										}}
										onSuccess={async () => {
											const credentials = await Auth.currentCredentials();
											const picture: BucketImage = {
												key: 'profilePic/customProfilePicture',
												level: 'protected',
												identityId: credentials.identityId
											};

											setValue('profilePicture', picture);
											setTemporaryPicture(picture);
										}}
									/>
								</Box>

								<Divider />
							</>
						)}

						{/* Personal information. */}
						<SectionHeader
							icon={<PersonRounded />}
							title="Personal information"
							description="Your name and personal details."
						/>
						<Stack spacing={1.75} sx={{ pb: 1.5 }}>
							<FieldRow>
								<FormTextField
									name="firstName"
									label="Legal First Name"
									control={control}
									rules={{ required: 'Required Field' }}
								/>
								<FormTextField
									name="lastName"
									label="Last Name"
									control={control}
									rules={{ required: 'Required Field' }}
								/>
								{!isSignUp && (
									<FormTextField name="preferredName" label="Preferred Name" control={control} />
								)}
							</FieldRow>
							{!isSignUp && (
								<FieldRow>
									<FormDatePicker
										name="dateOfBirth"
										label="Date Of Birth"
										control={control}
										rules={{ required: 'Required Field' }}
									/>
									<Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
										<FormSelect
											name="citizenship"
											label="Citizenship"
											control={control}
											rules={{ required: 'Required Field' }}
											items={countries.map((value) => ({
												label: `${value.flag} ${value.name}`,
												value: value.name
											}))}
										/>
									</Box>
								</FieldRow>
							)}
						</Stack>
					</Box>

					{/* PTEC-517: canonical education history in its OWN pane — pill-first
					    add flow + timeline; "most recent school attended" is DERIVED and
					    only ever shared via an explicit Lead-Share consent. */}
					{!isSignUp && (
						<Box sx={paneSx('education')}>
							<Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
								<EducationHistoryEditor
									value={formWatch.educationHistory ?? []}
									onChange={(next) => setValue('educationHistory', next, { shouldDirty: true })}
									jurisdiction={resolveEducationJurisdiction({
										isoCountryCode: formWatch.address?.isoCountryCode,
										citizenship: formWatch.citizenship
									})}
									defaultCountryIso={formWatch.address?.isoCountryCode ?? ''}
								/>
							</Box>
						</Box>
					)}

					{isSignUp && <Divider />}

					<Box sx={paneSx('address')}>
						{/* Address. */}
						<SectionHeader icon={<HomeRounded />} title="Address" description="Where you're based." />
						<Stack spacing={1.75} sx={{ pb: 1.5 }}>
							{!isSignUp && (
								<>
									<FieldRow>
										<AddressAutoComplete
											startingValue={getValues('address.zip') || ''}
											skipLengthCheck={true}
											returnPostcode={true}
											label={'Search by postcode'}
											size={'small'}
											widthProp={'100%'}
											onSelectAddress={(address) => {
												handleSelectedAddress(address);
											}}
										/>
									</FieldRow>
									<FieldRow>
										<FormTextField
											name="address.line1"
											label="Address Line 1"
											control={control}
											rules={{ required: 'Required Field' }}
										/>
										<FormTextField name="address.line2" label="Address Line 2" control={control} />
									</FieldRow>
								</>
							)}
							<FieldRow>
								<LocationSelector
									address={formWatch.address}
									skipCheck={true}
									returnObject={true}
									widthProp={'100%'}
									isRow
									disableCity
									disableState={isSignUp}
									onSelect={(location) => {
										if (typeof location === 'object') {
											const selectedCountry = location.country;
											// Compare before writing the new value. Re-deriving only on a
											// genuine country change means a currency the user picked by
											// hand survives a later city/state edit, while still following
											// them if they correct the country itself.
											const countryChanged =
												(selectedCountry?.name ?? '') !== getValues('address.country');

											setCountryISO(selectedCountry ? selectedCountry.isoCode : 'IE');
											setValue('address.country', selectedCountry ? selectedCountry.name : '');

											// Only while the account is being set up — the currency select is
											// shown under the same condition, and an established account's
											// currency is tied to orders already placed against it.
											if (!user?.confirmed && selectedCountry && countryChanged) {
												const derived = currencyForCountry(selectedCountry.currency);
												setValue('currency', derived);
												// Keep the app-wide currency in step with the form. The old
												// code skipped this on the unsupported-country path, leaving
												// prices rendered in a currency the profile did not hold.
												setCurrency(derived);
											}
											setValue('address.city', location.city ? location.city.name : '');
											setValue('address.state', location.state ? location.state.name : '');
										}
									}}
								></LocationSelector>
							</FieldRow>
							{!user?.confirmed && (
								<FieldRow>
									<FormSelect
										name="currency"
										label={t(
											'pages.admin.digitalProducts.masterBiller.inviteClient.step_1.currency'
										)}
										helperText="This is the currency we will use for your account"
										control={control}
										rules={{ required: 'Required Field' }}
										onChange={(e) => {
											setCurrency((e?.target as HTMLSelectElement).value);
										}}
										items={[
											{ value: 'gbp', label: 'GBP' },
											{ value: 'eur', label: 'EUR' },
											{ value: 'usd', label: 'USD' },
											{ value: 'aed', label: 'AED' }
										]}
									/>
								</FieldRow>
							)}
						</Stack>
					</Box>

					{isSignUp && <Divider />}

					<Box sx={paneSx('contact')}>
						{/* Contact information. */}
						<SectionHeader
							icon={<MailRounded />}
							title="Contact information"
							description="How we reach you."
						/>
						<Stack spacing={1.75} sx={{ pb: 1.5 }}>
							<FieldRow>
								<FormTextField
									name="email"
									label="Email"
									control={control}
									rules={{ required: 'Required Field' }}
									type="email"
								/>
								<FormMuiTelInput
									name="phone"
									label="Phone Number"
									control={control}
									getCountryFromAddress={getValues('address.isoCountryCode') || countryISO.toString()}
									isPhoneNumPresent={user?.phone}
									rules={{
										required: 'Required Field',
										maxLength: {
											value: 20,
											message: 'phone number must be between 3 and 20 numbers long'
										},
										minLength: {
											value: 5,
											message: 'phone number must be between 3 and 20 numbers long'
										}
									}}
								/>
							</FieldRow>
						</Stack>

						{isSignUp && <Divider />}
					</Box>

					<Box sx={paneSx('preferences')}>
						{/* Preferences. */}
						<SectionHeader
							icon={<LanguageRounded />}
							title={t('pages.profile.edit.preferences.title', 'Preferences')}
							description={t(
								'pages.profile.edit.preferences.description',
								'Language and regional settings.'
							)}
						/>
						<Stack spacing={1.75} sx={{ pb: 1.5 }}>
							<FieldRow>
								<Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
									<FormSelect
										name="language"
										label={t('pages.profile.edit.language.label', 'Language')}
										helperText={t(
											'pages.profile.edit.language.helperText',
											'The language used across the app'
										)}
										control={control}
										onChange={(e) => {
											setPreferredLanguage((e?.target as HTMLSelectElement).value);
										}}
										items={SUPPORTED_LANGUAGES.map((language) => ({
											value: language.code,
											label: language.label
										}))}
									/>
								</Box>
							</FieldRow>
						</Stack>
					</Box>

					{/* Footer actions. */}
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'flex-end',
							alignItems: 'center',
							gap: 1.5,
							borderTop: '1px solid',
							borderColor: 'divider',
							px: { xs: 2, sm: 3 },
							py: 1.5
						}}
					>
						{/* No cancel during sign-up: there is no completed profile to return
						    to, and leaving only bounces off the router guard that sent the user
						    here. Completing the form is the only way forward. */}
						{!isSignUp && (
							<Button onClick={() => navigate('/profile')} sx={cancelButtonSx}>
								Cancel
							</Button>
						)}
						<LoadingButton type="submit" variant="contained" loading={loading} sx={saveButtonSx}>
							{isSignUp ? 'Continue' : 'Save'}
						</LoadingButton>
					</Box>
				</form>
			</Card>

			<Dialog open={confirmEmailOpen} onClose={() => setConfirmEmailOpen(false)}>
				<DialogTitle>Changing Email</DialogTitle>
				<DialogContent>
					You are changing your email address. This will also affect your authentication credentials. Do you
					want to proceed?
				</DialogContent>
				<DialogActions>
					<Button sx={{ textTransform: 'none' }} onClick={handleCancelChangeEmail}>
						Cancel
					</Button>
					<Button sx={{ textTransform: 'none' }} variant="contained" onClick={handleChangeEmail}>
						Confirm
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default EditProfile;
